/**
 * THE ROUTE ENTRIES THIS TRACK OWNS.
 *
 * WHY THEY ARE PLAIN DESCRIPTORS AND NOT TanStack Router routes. When this file was written
 * `@tanstack/react-router` (ruled by ADR-0007) was not installed, so each entry below became the
 * data a route needs — a path, a component, and whether it is inside the shell. The router has
 * since landed and `src/router.tsx` turns each entry into a `createRoute`; the entries stayed data.
 *
 * THE COMPONENTS ARE THE SESSION-BOUND ROUTES, NOT THE BARE SCREENS. In a live build `/support`
 * reads the session the provider already holds and `/terms` is the gate's detour with its way back
 * (./session-routes.tsx); in the default build both render the bare screen exactly as before.
 *
 * WHY `/terms` IS DECLARED HERE. The terms interstitial belongs to this track's subject matter
 * (terms acceptance and re-acceptance) but not to `/support`'s path. This track's write scope is
 * `routes/support/**` and `features/support/**`, so the descriptor lives here rather than in a
 * `routes/terms/` directory another agent may also be creating. Moving it later is a file move and
 * nothing else; the component it names does not care.
 *
 * ROUTE PATHS ARE OURS. `adr/0007-web-app-stack.md` fixes only `/enter` and the magic-link
 * handler. Everything else is layout and navigation — decision-log row 99 as amended by row 117 —
 * so `/support` and `/terms` are decided here, following the UI track's own proposal
 * (docs/ui/04-pages.md, :81), and are not an owner question.
 */
import type { FunctionComponent } from 'react';
import { TERMS_PATH } from '../../features/auth/navigation';
import { SupportPageRoute, TermsInterstitialRoute } from './session-routes';

export interface CabinetRouteDescriptor {
  /** The URL path, absolute, no basename — the cabinet owns the whole of authors.moonx.app. */
  readonly path: string;
  /** What renders there. */
  /**
   * A FUNCTION component, not `ComponentType`. Narrowed on 2026-09-03 when the router was
   * wired: `@tanstack/react-router`'s `RouteComponent` does not accept a class component, so
   * the wider type would only have failed at the wiring point. Every screen here is already a
   * function; this states the constraint where the descriptor is written rather than casting
   * it away in `router.tsx`.
   */
  readonly Component: FunctionComponent;
  /**
   * Whether the reader must be signed in. Both of these are authenticated: the cabinet has exactly
   * two unauthenticated routes and neither is one of these (`adr/0007`).
   */
  readonly requiresSession: true;
  /**
   * Whether the route renders inside the cabinet's nav shell.
   *
   * `/terms` does NOT. It is an interstitial: an author who must accept before submitting,
   * resubmitting or publishing should not be able to wander off into the editor from it, and the
   * corpus describes it as "one interstitial screen with one click" (`04-schema.md`).
   * `/support` does — it is an ordinary cabinet view, ninth in the prototype's own nav
   * (`{key:'support', label:'Support', icon:'circle-help'}`).
   */
  readonly insideShell: boolean;
  /** The nav label, when it appears in the nav at all. The prototype's own word. */
  readonly navLabel: string | null;
}

export const SUPPORT_ROUTES: readonly CabinetRouteDescriptor[] = [
  {
    path: '/support',
    Component: SupportPageRoute,
    requiresSession: true,
    insideShell: true,
    navLabel: 'Support',
  },
  {
    // The gate sends people to this constant (features/auth/navigation.ts), so it is read, not typed.
    path: TERMS_PATH,
    Component: TermsInterstitialRoute,
    requiresSession: true,
    insideShell: false,
    navLabel: null,
  },
];
